import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Privacy Policy"
        description="Genora LLC privacy policy. Learn how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, personal information"
      />
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
          <p className="text-sm text-slate-500">Last updated: January 2024</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              Genora LLC ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you visit our website 
              or purchase our research products.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">2. Information We Collect</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We may collect the following types of information:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Personal identification information (name, email address, phone number)</li>
              <li>Shipping and billing addresses</li>
              <li>Payment information (processed securely through third-party providers)</li>
              <li>Research institution or organization affiliation</li>
              <li>Order history and preferences</li>
              <li>Website usage data and analytics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We use the collected information for:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Processing and fulfilling orders</li>
              <li>Providing customer support</li>
              <li>Sending order updates and shipping notifications</li>
              <li>Improving our website and services</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">4. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. However, no 
              method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">5. Third-Party Services</h2>
            <p className="text-slate-600 leading-relaxed">
              We may use third-party service providers to facilitate our services, including payment processing 
              and analytics. These third parties have access to your personal information only to perform 
              specific tasks on our behalf and are obligated to protect your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">6. Your Rights</h2>
            <p className="text-slate-600 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal data</li>
              <li>Opt-out of marketing communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">7. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at{' '}
              <Link to="/contact" className="text-teal-700 hover:underline">our contact page</Link>.
            </p>
          </section>
        </div>

        {/* Research Disclaimer */}
        <div className="mt-12 p-6 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-4">
            <Shield className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-heading font-semibold text-amber-800 mb-2">Research Use Only</h3>
              <p className="text-sm text-amber-700">
                All products sold by Genora LLC are intended for laboratory research purposes only. 
                They are not intended for human or animal consumption.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
